import { isNull } from 'lodash/fp';
import { RawDraftContentState } from 'draft-js';

import { ApplicationState } from '../../../../store/types';
import { OpinionReportFilter, OpinionReportTemplate, OpinionScopeFields, ReportPage, ReportTab, UpsertOpinionReportTemplate } from './types';
import { OpinionScope } from '../../../admin/opinions/store/types';
import { getOpinionReportingRoot } from '../../store/selectors';

export const getReportPage = (state: ApplicationState): ReportPage => getOpinionReportingRoot(state).reportPage;

export const getSelectedTab = (state: ApplicationState): ReportTab => getOpinionReportingRoot(state).selectedTab;

export const getReportFilters = (state: ApplicationState): OpinionReportFilter => getOpinionReportingRoot(state).reportFilters;

export const getReportFields = (state: ApplicationState): OpinionScopeFields => getOpinionReportingRoot(state).reportFields;

export const getOpinionScope = (state: ApplicationState): OpinionScope[] => getOpinionReportingRoot(state).opinionScope;

export const getCurrentScope = (state: ApplicationState): OpinionScope | null => getOpinionReportingRoot(state).currentScope;

export const getIsLoading = (state: ApplicationState): boolean => getOpinionReportingRoot(state).fetchingAvailableFields;

export const getIsGeneratingReport = (state: ApplicationState): boolean => getOpinionReportingRoot(state).generatingReport;

export const getAvailableFieldsError = (state: ApplicationState): null | string => getOpinionReportingRoot(state).fetchingError;

export const getBackConfirmationModalOpen = (state: ApplicationState): boolean => getOpinionReportingRoot(state).backConfirmationModalOpen;

export const getReportUpdated = (state: ApplicationState): boolean => getOpinionReportingRoot(state).reportUpdated;

export const getAllReportTemplates = (state: ApplicationState): OpinionReportTemplate[] => getOpinionReportingRoot(state).reportTemplates;

export const getSaveTemplateModalOpen = (state: ApplicationState): boolean => getOpinionReportingRoot(state).saveTemplateModalOpen;

export const getGenerateModalOpen = (state: ApplicationState): boolean => getOpinionReportingRoot(state).generateModalOpen;

export const getReportTemplateName = (state: ApplicationState): string => getOpinionReportingRoot(state).templateName;
export const getReportTemplateDescription = (state: ApplicationState): string => getOpinionReportingRoot(state).templateDescription;
export const getReportSelectedUserIds = (state: ApplicationState): number[] => getOpinionReportingRoot(state).templateUserIds;
export const getReportEmailBody = (state: ApplicationState): RawDraftContentState | null => getOpinionReportingRoot(state).templateEmailBody;

export const getSelectedTemplate = (state: ApplicationState): OpinionReportTemplate | null => getOpinionReportingRoot(state).selectedTemplate;

export const getSelectedTemplateId = (state: ApplicationState): number | undefined => getSelectedTemplate(state)?.opinionReportTemplateId;

export const getConfirmDeleteReportTemplate = (state: ApplicationState): number | null => getOpinionReportingRoot(state).confirmDeleteReportTemplate;

export const getConfirmDeleteModalOpen = (state: ApplicationState): boolean => !isNull(getConfirmDeleteReportTemplate(state));

export const getIsDeleting = (state: ApplicationState): boolean => getOpinionReportingRoot(state).isDeleting;

export const getUpsertReportTemplate = (state: ApplicationState): UpsertOpinionReportTemplate => {
    const reportFields = getReportFields(state);
    const name: string = getReportTemplateName(state);
    const description: string = getReportTemplateDescription(state);
    const opinionReportTemplateId = getSelectedTemplateId(state);
    const filters = getReportFilters(state);
    const includeSignOffNotes = getIncludeSignOffNotes(state);
    return {
        name,
        description,
        opinionReportTemplateId,
        reportFields,
        filters,
        includeSignOffNotes: includeSignOffNotes ? 1 : 0,
        isOpinionReport: true
    };
};

export const getIsFetchingTemplates = (state: ApplicationState): boolean => getOpinionReportingRoot(state).fetchingTemplates;

export const getOpenScopeSections = (state: ApplicationState): string[] => {
    const currentScope = getCurrentScope(state);
    const openSections = getOpinionReportingRoot(state).openScopeSections;
    return isNull(currentScope) ? [] : openSections[currentScope];
};

export const getIncludeSignOffNotes = (state: ApplicationState): boolean => getOpinionReportingRoot(state).includeSignOffNotes;
